import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Box, Tooltip } from '@mui/material'

import { apiZodios } from '~api/apiZodios'
import { useListDetailedIdentities } from '~hooks/useListDetailedIdentities'
import AddContactDialog from '~pages/ContactsScreen/v2/components/AddContactDialog'
import { type RootState } from '~redux/rootReducer'
import { useListIdentitiesByIdsQuery, useListStaffersQuery } from '~redux/slices/apiSlice'
import { lightTokens, typography } from '~tokens/internal'
import {
  type ContactEntryId,
  type EmailIdentity,
  type Identity0,
  type Identity2,
  type IdentityId,
  type TargetId,
} from '~types'

import Avatar from '../Avatar'
import Chip from '../Chip'
import Dropdown, { type DropdownOption } from '../Dropdown'
import { formatPhoneNumber } from '../formatPhoneNumber'
import Icon from '../Icon'
import IconButton from '../IconButton'

import './IdentitySelector.scss'

// --- Constants ---

const SEARCH_LIMIT = 6

// --- Helpers ---

const renderIdentityDetails = (identity: Identity2): React.ReactNode => {
  const emails = Array.from(new Set(identity.emails?.map((e) => e.address).filter(Boolean) ?? []))
  const phones = Array.from(
    new Set((identity.phones ?? []).filter(Boolean).map((phone) => formatPhoneNumber(phone))),
  )
  const addresses = Array.from(new Set((identity.addresses ?? []).filter(Boolean)))

  return (
    <Box className='internal-identity-selector__details'>
      {emails.length > 0 && (
        <Box className='internal-identity-selector__detail-row'>
          <Icon name='Email' size={typography.fontSize.sm} />
          {emails.join(', ')}
        </Box>
      )}
      {phones.length > 0 && (
        <Box className='internal-identity-selector__detail-row'>
          <Icon name='Phone' size={typography.fontSize.sm} />
          {phones.join(', ')}
        </Box>
      )}
      {addresses.length > 0 && (
        <Box className='internal-identity-selector__detail-row'>
          <Icon name='LocationOn' size={typography.fontSize.sm} />
          {addresses.join(', ')}
        </Box>
      )}
    </Box>
  )
}

// --- Component ---

export interface IdentitySelectorProps {
  // Standard mode (detailed=false): staff/user identities (Identity0)
  selectedIdentities?: Identity0[]
  onChange?: (selected: Identity0[]) => void

  // Detailed mode (detailed=true), single-select: constituent identity (Identity2)
  // Legacy: pass full object
  selectedIdentity?: Identity2 | null
  onIdentityChange?: (selected: Identity2 | null) => void
  // New: pass just the ID (simpler when using identityPool)
  selectedIdentityId?: string | null
  onIdentityIdChange?: (id: string | null, identity: Identity2 | null) => void

  // Detailed mode (detailed=true), multi-select: constituent identities (Identity2[])
  // Legacy: pass full objects
  selectedDetailedIdentities?: Identity2[]
  onDetailedIdentitiesChange?: (selected: Identity2[]) => void
  // New: pass just the IDs
  selectedDetailedIdentityIds?: string[]
  onDetailedIdentityIdsChange?: (ids: string[], identities: Identity2[]) => void

  // Optional pre-fetched pool of identities for client-side search in detailed mode.
  // When provided, skips the API call and filters this list instead.
  identityPool?: Identity2[]

  // IDs to exclude from the dropdown options (e.g., already-selected members)
  excludeIds?: string[]

  // Detailed mode only: restrict results to contacts/parties, excluding org staffers
  excludeStaff?: boolean

  // Show a clear button to remove the current selection
  showClearButton?: boolean

  organizationIdentityIdList?: IdentityId[]
  bookIdentityId?: IdentityId
  multiSelect?: boolean
  detailed?: boolean
  label?: string
  disabled?: boolean
  showAddContact?: boolean
  organization?: EmailIdentity | null
  placeholder?: string
  closeOnSelect?: boolean
}

const IdentitySelector: React.FC<IdentitySelectorProps> = ({
  selectedIdentities = [],
  selectedIdentity = null,
  onChange,
  onIdentityChange,
  selectedIdentityId,
  onIdentityIdChange,
  selectedDetailedIdentities = [],
  onDetailedIdentitiesChange,
  selectedDetailedIdentityIds,
  onDetailedIdentityIdsChange,
  identityPool,
  excludeIds = [],
  excludeStaff = false,
  showClearButton = false,
  organizationIdentityIdList,
  bookIdentityId,
  multiSelect = false,
  detailed = false,
  label,
  disabled = false,
  showAddContact = false,
  organization,
  placeholder,
  closeOnSelect = false,
}) => {
  const userId = useSelector((state: RootState) => state?.auth.userId)
  const selectedOrganization = useSelector(
    (state: RootState) => state?.organization.selectedOrganization,
  )
  const selectedOrganizationId = selectedOrganization?.id as IdentityId | undefined
  const orgId: IdentityId | undefined = bookIdentityId ?? selectedOrganizationId

  const [addContactDialogOpen, setAddContactDialogOpen] = useState(false)

  const inputOrganization = organization || selectedOrganization

  const handleAddContactSuccess = async (
    _contactEntryId: ContactEntryId,
    identityId: IdentityId,
    _targetId: TargetId,
  ): Promise<void> => {
    setAddContactDialogOpen(false)

    if (!userId) return
    try {
      const response = await apiZodios.core_listIdentitiesByIds({
        user_id: userId,
        identity_ids: [identityId],
      })
      const identity = response.identities[0]
      if (!identity) return

      if (detailed) {
        if (multiSelect) {
          const newSelected = [...selectedDetailedIdentities, identity as Identity2]
          const currentIds =
            selectedDetailedIdentityIds ?? selectedDetailedIdentities.map((i) => i.id)
          const newIds = [...currentIds, identity.id]
          onDetailedIdentitiesChange?.(newSelected)
          onDetailedIdentityIdsChange?.(newIds, newSelected)
        } else {
          onIdentityChange?.(identity as Identity2)
          onIdentityIdChange?.(identity.id, identity as Identity2)
        }
      } else {
        onChange?.([identity as unknown as Identity0])
      }
    } catch (error) {
      console.error('Failed to fetch newly created contact:', error)
    }
  }

  // ===== Detailed (constituent) mode =====

  const [detailedQuery, setDetailedQuery] = useState('')

  const selectedIdToFetch =
    detailed && selectedIdentityId && !selectedIdentity ? selectedIdentityId : undefined
  const { data: selectedIdentityData } = useListIdentitiesByIdsQuery(
    { user_id: userId || '', identity_ids: selectedIdToFetch ? [selectedIdToFetch] : [] },
    { skip: !userId || !selectedIdToFetch, refetchOnMountOrArgChange: true },
  )

  // Fetch identity objects for multi-select IDs not already provided as full objects
  const selectedIdsToFetch = useMemo(() => {
    if (!detailed || !multiSelect || !selectedDetailedIdentityIds?.length) return []
    const knownIds = new Set(selectedDetailedIdentities.map((i) => i.id))
    return selectedDetailedIdentityIds.filter((id) => !knownIds.has(id))
  }, [detailed, multiSelect, selectedDetailedIdentityIds, selectedDetailedIdentities])
  const { data: selectedIdsData } = useListIdentitiesByIdsQuery(
    { user_id: userId || '', identity_ids: selectedIdsToFetch },
    { skip: !userId || selectedIdsToFetch.length === 0 },
  )

  const { data: detailedData, isFetching: detailedSearching } = useListDetailedIdentities(
    {
      user_id: userId || '',
      organization_identity_id_list: orgId ? [orgId] : [],
      limit: SEARCH_LIMIT,
      query: detailedQuery,
      ...(bookIdentityId ? { book_identity_id: bookIdentityId } : {}),
      exclude_staff: excludeStaff,
    },
    { skip: !userId || !detailed || !!identityPool },
  )
  const detailedResults: Identity2[] = identityPool
    ? identityPool
        .filter((i) => {
          if (!detailedQuery) return true
          const q = detailedQuery.toLowerCase()
          return (
            i.name?.toLowerCase().includes(q) ||
            i.emails?.some((e) => e.address?.toLowerCase().includes(q)) ||
            i.phones?.some((p) => p?.toLowerCase().includes(q))
          )
        })
        .slice(0, SEARCH_LIMIT)
    : (detailedData?.identities ?? [])

  const detailedIdentityMap = useMemo(() => {
    const map = new Map<string, Identity2>()
    if (selectedIdentity) map.set(selectedIdentity.id, selectedIdentity)
    const fetchedIdentity = selectedIdentityData?.identities?.[0]
    if (fetchedIdentity) map.set(fetchedIdentity.id, fetchedIdentity)
    for (const i of selectedIdsData?.identities ?? []) map.set(i.id, i)
    for (const i of selectedDetailedIdentities) map.set(i.id, i)
    for (const i of detailedResults) map.set(i.id, i)
    return map
  }, [
    selectedIdentity,
    selectedIdentityData,
    selectedIdsData,
    selectedDetailedIdentities,
    detailedResults,
  ])

  // Get the effective selected ID (from either old or new prop)
  const effectiveSelectedId = selectedIdentityId ?? selectedIdentity?.id ?? undefined

  // Get the effective selected IDs for multi-select (from either old or new prop)
  // Filter out excluded IDs so they don't show in the input
  const effectiveSelectedIds = (
    selectedDetailedIdentityIds ?? selectedDetailedIdentities.map((i) => i.id)
  ).filter((id) => !excludeIds.includes(id))

  const detailedOptions: DropdownOption[] = useMemo(() => {
    const items = Array.from(detailedIdentityMap.values()).filter((i) => !excludeIds.includes(i.id))
    return items.map((i) => ({
      value: i.id,
      label: i.name || i.emails?.find((email) => email.address)?.address || '',
      detailed_information: renderIdentityDetails(i),
    }))
  }, [detailedIdentityMap, excludeIds])

  const handleDetailedChange = (value: string | string[]): void => {
    if (multiSelect) {
      const ids = Array.isArray(value) ? value : [value]
      const selected = ids.map((id) => detailedIdentityMap.get(id)).filter(Boolean) as Identity2[]
      // Support both old and new callback APIs
      onDetailedIdentitiesChange?.(selected)
      onDetailedIdentityIdsChange?.(ids, selected)
    } else {
      const id = Array.isArray(value) ? value[0] : value
      const identity = detailedIdentityMap.get(id) ?? null
      // Support both old and new callback APIs
      onIdentityChange?.(identity)
      onIdentityIdChange?.(id || null, identity)
    }
  }

  const handleClear = (): void => {
    if (multiSelect) {
      onDetailedIdentitiesChange?.([])
      onDetailedIdentityIdsChange?.([], [])
    } else {
      onIdentityChange?.(null)
      onIdentityIdChange?.(null, null)
    }
  }

  const renderDetailedSelectedValue = (opt: DropdownOption): React.ReactNode => {
    const identity = detailedIdentityMap.get(opt.value)
    const chipLabel = identity?.name || opt.label
    return (
      <Chip
        label={chipLabel}
        size='small'
        onDelete={(e) => {
          e.stopPropagation()
          const newIds = effectiveSelectedIds.filter((id) => id !== opt.value)
          const newSelected = newIds
            .map((id) => detailedIdentityMap.get(id))
            .filter(Boolean) as Identity2[]
          onDetailedIdentitiesChange?.(newSelected)
          onDetailedIdentityIdsChange?.(newIds, newSelected)
        }}
      />
    )
  }

  // ===== Standard (staff) mode =====

  const [search, setSearch] = useState('')
  const effectiveOrgIds = organizationIdentityIdList?.length
    ? organizationIdentityIdList
    : selectedOrganizationId
      ? [selectedOrganizationId]
      : []
  const { data, isFetching } = useListStaffersQuery(
    {
      user_id: userId || '',
      organization_identity_id_list: effectiveOrgIds,
      limit: SEARCH_LIMIT,
      query: search,
    },
    { skip: !userId || detailed },
  )
  const results: Identity0[] = data?.identities ?? []

  const identityMap: Map<string, Identity0> = useMemo(() => {
    return new Map<string, Identity0>([
      ...selectedIdentities.map((identity): [string, Identity0] => [identity.id, identity]),
      ...results.map((identity): [string, Identity0] => [identity.id, identity]),
    ])
  }, [selectedIdentities, results])

  const options: DropdownOption[] = useMemo(() => {
    const items = Array.from(identityMap.values())
    return items.map((i) => ({
      value: i.id,
      label: i.name || i.email || '',
      avatar: <Avatar size='sm' identity={i} alt={i.name || i.email || ''} />,
    }))
  }, [identityMap])

  const handleChange = (value: string | string[]): void => {
    const ids = Array.isArray(value) ? value : [value]
    const newSelection = ids.map((id) => identityMap.get(id)).filter(Boolean) as Identity0[]
    onChange?.(newSelection)
  }

  const renderSelectedValue = (opt: DropdownOption): React.ReactNode => {
    const identity = identityMap.get(opt.value)
    const chipLabel = identity ? identity.name || identity.email || '' : opt.label
    return (
      <Chip
        label={chipLabel}
        size='small'
        avatar={<Avatar size='sm' identity={identity} alt={chipLabel} />}
        onDelete={(e) => {
          e.stopPropagation()
          if (multiSelect) {
            onChange?.(selectedIdentities.filter((i) => i.id !== opt.value))
          } else {
            onChange?.([])
          }
        }}
      />
    )
  }

  // ===== Render =====

  const actionButton = showAddContact ? (
    <Box className='internal-identity-selector__add-button'>
      <Tooltip title='Create contact'>
        <IconButton size='small' onClick={() => setAddContactDialogOpen(true)}>
          <Icon name='addCircle' color={lightTokens.primary.main} />
        </IconButton>
      </Tooltip>
    </Box>
  ) : null

  if (detailed) {
    const detailedDropdown = (
      <Dropdown
        options={detailedOptions}
        value={multiSelect ? effectiveSelectedIds : effectiveSelectedId}
        onChange={handleDetailedChange}
        label={label}
        placeholder={placeholder ?? 'Select assignee'}
        disabled={disabled}
        multiple={multiSelect}
        closeOnSelect={closeOnSelect}
        searchable
        onSearchChange={setDetailedQuery}
        searchValue={detailedQuery}
        loading={!identityPool && detailedSearching}
        renderValue={multiSelect ? renderDetailedSelectedValue : undefined}
        startAdornment={<Icon name='person' />}
        endAdornment={
          showClearButton &&
          (multiSelect ? effectiveSelectedIds.length > 0 : effectiveSelectedId != null) ? (
            <IconButton
              size='small'
              onClick={(e) => {
                e.stopPropagation()
                handleClear()
              }}
            >
              <Icon fontSize='small' name='close' />
            </IconButton>
          ) : null
        }
        fullWidth
      />
    )

    if (showAddContact) {
      return (
        <>
          <Box className='internal-identity-selector__with-action'>
            <Box className='internal-identity-selector__dropdown-with-button'>
              {detailedDropdown}
              {actionButton}
            </Box>
          </Box>
          <AddContactDialog
            open={addContactDialogOpen}
            onSaveSuccess={handleAddContactSuccess}
            onClose={() => setAddContactDialogOpen(false)}
            inputOrganization={inputOrganization}
          />
        </>
      )
    }

    return detailedDropdown
  }

  const dropdownContent = (
    <Dropdown
      options={options}
      value={multiSelect ? selectedIdentities.map((i) => i.id) : selectedIdentities[0]?.id}
      onChange={handleChange}
      label={label}
      placeholder={placeholder ?? 'Select manager'}
      disabled={disabled}
      multiple={multiSelect}
      closeOnSelect={closeOnSelect}
      searchable
      onSearchChange={setSearch}
      searchValue={search}
      loading={isFetching}
      renderValue={multiSelect ? renderSelectedValue : undefined}
      startAdornment={<Icon name='personSearch' />}
      endAdornment={
        showClearButton && selectedIdentities.length > 0 ? (
          <IconButton
            size='small'
            onClick={(e) => {
              e.stopPropagation()
              onChange?.([])
            }}
          >
            <Icon fontSize='small' name='close' />
          </IconButton>
        ) : null
      }
      fullWidth
    />
  )

  if (showAddContact) {
    return (
      <>
        <Box className='internal-identity-selector__with-action'>
          <Box className='internal-identity-selector__dropdown-with-button'>
            {dropdownContent}
            {actionButton}
          </Box>
        </Box>
        <AddContactDialog
          open={addContactDialogOpen}
          onSaveSuccess={handleAddContactSuccess}
          onClose={() => setAddContactDialogOpen(false)}
          inputOrganization={inputOrganization}
        />
      </>
    )
  }

  return dropdownContent
}

export default IdentitySelector
